import React from 'react';
import { FaRocket, FaPlay, FaInfoCircle, FaClock, FaCheck, FaTimes, FaTrash } from 'react-icons/fa';
import ToggleButtonGroup from './ToggleButtonGroup';

interface GeocodingControlsProps {
  selectionMode: string;
  onSelectionModeChange: (mode: string) => void;
  selectedPropertiesCount: number;
  totalPropertiesCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  propertiesToGeocode: number;
  onStartGeocoding: () => void;
  isGeocoding: boolean;
  missingCoordinatesCount: number;
  modeAutoSwitched: boolean;
  onClearCoordinates: () => void;
  isClearingCoordinates: boolean;
  selectedCompany: any;
}

const GeocodingControls: React.FC<GeocodingControlsProps> = ({
  selectionMode,
  onSelectionModeChange,
  selectedPropertiesCount,
  totalPropertiesCount,
  onSelectAll,
  onDeselectAll,
  propertiesToGeocode,
  onStartGeocoding,
  isGeocoding,
  missingCoordinatesCount,
  modeAutoSwitched,
  onClearCoordinates,
  isClearingCoordinates,
  selectedCompany
}) => {
  // Calculate the display count based on selection mode
  const getDisplayCount = () => {
    if (selectionMode === 'all-missing') {
      return `${missingCoordinatesCount} of ${missingCoordinatesCount}`;
    } else if (selectionMode === 'selected-only') {
      return `${selectedPropertiesCount} of ${totalPropertiesCount}`;
    } else {
      return `0 of ${totalPropertiesCount}`;
    }
  };

  // Selection mode options
  const selectionOptions = [
    { value: 'all-missing', label: 'All Missing Coordinates', icon: FaClock },
    { value: 'selected-only', label: 'Selected Only', icon: FaCheck }
  ];

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border">
      <div className="flex items-center gap-3 mb-6">
        <FaRocket size={20} className="text-purple-600" />
        <h2 className="text-lg font-semibold text-gray-900">Geocoding Controls</h2>
      </div>

      {/* Property Selection Mode */}
      <div className="mb-6">
        {modeAutoSwitched && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-blue-800">
              <FaInfoCircle size={16} />
              <span className="text-sm font-medium">
                Mode automatically switched to "Selected Only" because you manually changed property selections
              </span>
            </div>
          </div>
        )}
        
        <h3 className="text-sm font-medium text-gray-700 mb-3">Property Selection Mode</h3>
        <ToggleButtonGroup
          options={selectionOptions}
          value={selectionMode}
          onChange={onSelectionModeChange}
          variant="primary"
        />
      </div>

      {/* Selection Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-sm text-gray-600">
          {getDisplayCount()} properties selected
        </span>
        
        <div className="flex gap-2">
          <button
            onClick={onSelectAll}
            disabled={!totalPropertiesCount}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Select All
          </button>
          <button
            onClick={onDeselectAll}
            disabled={!selectedPropertiesCount}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Deselect All
          </button>
        </div>
      </div>

      {/* Status & Action */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-lg font-semibold text-gray-900">
              Ready to geocode {propertiesToGeocode} properties
            </div>
            <div className="text-xs text-gray-500">
              {selectionMode === 'all-missing' 
                ? 'All properties missing coordinates will be processed'
                : 'Only selected properties will be processed'
              }
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClearCoordinates}
              disabled={!selectedCompany || isClearingCoordinates}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
            >
              {isClearingCoordinates ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Clearing...
                </>
              ) : (
                <>
                  <FaTrash size={16} />
                  Clear Coordinates
                </>
              )}
            </button>
            <button
              onClick={onStartGeocoding}
              disabled={propertiesToGeocode === 0 || isGeocoding}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
            >
              {isGeocoding ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Geocoding...
                </>
              ) : (
                <>
                  <FaPlay size={16} />
                  Start Geocoding
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeocodingControls;
